import { Refresh } from '@mui/icons-material';
import { Button, Grid } from '@mui/material';
import { FilterList, paramsKey } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { PublicationWrapper } from 'src/components/header/actionBar/ActionBar.styled';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useParamQuery } from 'src/hooks';
import { IFilter } from 'src/interfaces';
import { lang, mapEscapeRegExWithExclusions } from 'src/utils';

import { useListPublicationListPage } from './hooks/ListPublicationPage.hook';
import { PublicationListGridTable } from './modules/PublicationListGridTable/PublicationListGridTable';
import { PUBLICATION_STATUS } from './PublicationPage.interface';
import {
  ContentContainer,
  FooterContainer,
  PageContainer,
} from './PublicationPage.style';

export const PublicationPage = () => {
  const { getParamsValue } = useParamQuery();
  const { onChangeParams, refetchPublications, totalCount, getTotalResult } =
    useListPublicationListPage();

  const filters: IFilter[] = [
    {
      labelOverride: lang.status,
      label: 'publication_status',
      options: [
        {
          label: lang.outOfDate,
          value: [PUBLICATION_STATUS.OUT_OF_DATE],
        },
        {
          label: lang.updateInProgress,
          value: [PUBLICATION_STATUS.UPDATE_IN_PROGRESS],
        },
        {
          label: lang.uptoDate,
          value: [PUBLICATION_STATUS.UP_TO_DATE],
        },
        {
          label: `${lang.uptoDate} (${lang.offline})`,
          value: [PUBLICATION_STATUS.UP_TO_DATE_OFFLINE],
        },
      ],
    },
    {
      label: lang.timeZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      labelOverride: lang.publicationDateRange,
      label: 'date_range',
      fieldType: 'dateRange',
      options: [],
      dateRangeOption: {
        disableFutureDate: true,
      },
    },
  ];

  const { onRemovePublicationKeyword } = useFilterAndSorting(filters);

  const onReset = () => {
    onChangeParams('hq.name_or_store_number.regexp', []);
    filters.forEach(({ label }) => {
      if (label === 'date_range') {
        for (const key of paramsKey(label).split('|')) {
          onChangeParams(key, []);
        }
      } else {
        onChangeParams(paramsKey(label), []);
      }
    });
  };

  const onHandleRefresh = () => {
    refetchPublications();
  };

  const onChangeKeyword = (value: string | string[]) => {
    const escapedStrings = mapEscapeRegExWithExclusions(value, []);
    onChangeParams('hq.name_or_store_number.regexp', escapedStrings);
  };

  return (
    <PageContainer>
      <ContentContainer>
        <Grid>
          <Header
            breadcrumbItems={[
              {
                label: lang.locations,
                link: `${PATHS.LOCATIONS}`,
              },
              {
                label: lang.publications,
                link: `${PATHS.LIST_PUBLICATIONS}`,
              },
            ]}
            title={lang.publications}
            actionBar={
              <PublicationWrapper>
                <ActionBar
                  type={lang.publications}
                  numResults={getTotalResult}
                  maxResults={totalCount}
                />
              </PublicationWrapper>
            }
          />

          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FilterList
                filters={filters}
                onChangeKeyword={onChangeKeyword}
                onRemoveKeyword={onRemovePublicationKeyword}
                onChangeFilter={onChangeParams}
                showReset
                onReset={onReset}
                searchVal={getParamsValue('hq.name_or_store_number.regexp')}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <PublicationListGridTable />
            </Grid>
          </Grid>
        </Grid>
      </ContentContainer>
      <FooterContainer>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onHandleRefresh}>
          {lang.refresh}
        </Button>
      </FooterContainer>
    </PageContainer>
  );
};
